import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountService,
  AuthModalOptions,
  AuthService,
  BillingV2Service,
  CollectionsService,
  ContextService,
  FilesService,
  PlatformService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadMe } from '@store/actions/profile.actions';
import {
  BillingOperation,
  Collection,
  CollectionAccessEnum,
  CollectionDetailsType,
  CollectionLicenseEnum,
  IAccountStats,
  ImageSizeEnum,
  ModerationStatusEnum,
  SubjectCategoryEnum,
  User,
  UserRoleEnum,
  UserSegmentEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, PopoverPositionEnum } from 'onclass-ui';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type CollectionDisplayMode = 'default' | 'infinity' | 'content' | 'lesson-collection';

enum CollectionsCardActionsEnum {
  delete,
  complain,
}

interface IStatusOption {
  value: ModerationStatusEnum;
  message: string;
  class: string;
}

@Component({
  selector: 'app-collection-card-mini',
  templateUrl: './collection-card-mini.component.html',
  styleUrls: ['./collection-card-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionCardMiniComponent extends ChangableComponent implements OnInit {
  @Input() public set collection(collection: Collection) {
    this._collection = collection;
    this.prepareCard();
  }
  public get collection(): Collection {
    return this._collection;
  }
  @Input() public meta = true;
  @Input() public displayMode: CollectionDisplayMode = 'default';
  @Input() public linkEnable = true;
  @Output() public action = new EventEmitter<CollectionsCardActionsEnum>();
  @Output() public openInModal = new EventEmitter<Collection>();
  @Output() favoriteChanged = new EventEmitter<boolean>();

  public collectionUrl: string;
  public editCollectionUrl: string;
  public subjectIsNative = false;
  public selectedLicense: CollectionLicenseEnum;
  public authorName: string;
  public authorAvatarUrl: string;
  public collectionDate: Date;
  public accountRoute: string[];
  public isValid: boolean;
  public fullTitle: string;
  public multiCoverUrls: string[] = [];
  public isDeleteAlertOpened = false;
  public complainFormOpened = false;
  public purchaseOperation: BillingOperation;
  public position: PopoverPositionEnum = PopoverPositionEnum.bottom;
  public betterLicense: CollectionLicenseEnum;
  public buyCollectionModal = false;
  public userAvatar: OnUiCover;
  public authorStats: Partial<IAccountStats>;

  private _collection: Collection;
  public me: User;
  public user: User;
  private collectionRoute = LibraryRouter.collectionRouteUniversal;

  public moderationStatuses: IStatusOption[] = [
    {
      value: ModerationStatusEnum.default,
      message: 'collections.moderation-status.on-moderation',
      class: 'on-moderation',
    },
    {
      value: ModerationStatusEnum.softDeclined,
      message: 'collections.moderation-status.fail',
      class: 'fail',
    },
  ];
  public noCover: OnUiCover = {
    form: 'base',
    type: 'cover',
  };

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    public store: Store,
    private collectionsService: CollectionsService,
    private billingV2Service: BillingV2Service,
    private contextService: ContextService,
    private authService: AuthService,
    private accountService: AccountService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    public router: Router,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.suggestLicense();
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.store.dispatch(loadMe());
      },
    });
  }

  public coverImg(imageUrl: string): OnUiCover {
    return {
      img: imageUrl,
      form: 'base',
      type: 'cover',
    };
  }

  public get moderationStatus(): IStatusOption {
    return this.moderationStatuses.find((el) => el.value === this.collection.moderation_status);
  }

  public get isOnModeration(): boolean {
    return this.collection.moderation_status === ModerationStatusEnum.default;
  }

  public get isApproved(): boolean {
    return (
      this.collection.moderation_status === ModerationStatusEnum.approved &&
      this.collection.access === CollectionAccessEnum.commercial
    );
  }

  public get visit_info() {
    return this._collection?.visit_info || {};
  }

  public getFormattedPrice(amount: number, discount?: number): Observable<string> {
    return this.billingV2Service.formatAmount$(amount, discount);
  }

  public coverCssClasses(count: number, index: number): any {
    switch (count) {
      case 1:
        return { narrow: false, short: false };
      case 2:
        return { narrow: false, short: true };
      case 3:
        return index === 0 ? { narrow: false, short: true } : { narrow: true, short: true };
      case 4:
        return { narrow: true, short: true };
      default:
        return { narrow: false, short: false };
    }
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  private getAuthorDetails(user: User) {
    if (!user) {
      return;
    }

    this.user = user;
    this.authorName = AccountService.getUserFullName(user);
    this.authorAvatarUrl = this.user?.__opts?.images[0].sm
      ? this.prepareStorageUrl(this.user?.__opts?.images[0].sm)
      : FilesService.getFileUrlBySize(user.userpic_id, ImageSizeEnum.sm);
    this.accountRoute = AccountService.accountRoute(user);
    this.authorStats = user.stats;
    this.userAvatar = {
      type: 'avatar',
      img: this.authorAvatarUrl,
      title: this.authorName,
      link: this.accountRoute.join('/'),
      form: 'circle',
    };
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.collection.author = user;
          this.getAuthorDetails(user);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private prepareCard() {
    if (this._collection.author) {
      this.getAuthorDetails(this._collection.author);
    } else {
      this.loadAuthorDetails(this.collection.author_id);
    }
    this.fullTitle = this.collectionsService.collectionFullTitle(this._collection);
    this.collectionUrl = this.collectionRoute(this._collection);
    this.editCollectionUrl = '/content/collections/edit/' + this._collection.id;
    this.subjectIsNative = this._collection?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.collectionDate = this._collection
      ? this._collection.changed_at
        ? new Date(this._collection.changed_at)
        : new Date(this._collection.created_at)
      : new Date();
    this.isValid = this._collection?.valid;
    this.prepareCover();
    this.detectChanges();
  }

  public get isAuthor(): boolean {
    const me_id = this.me?.id;
    const author_id = this._collection?.author?.id || this._collection?.author_id;
    return me_id === author_id;
  }

  public get isModerator(): boolean {
    return this.me && AccountService.hasRole(this.me, UserRoleEnum.moderator);
  }

  public onToggleDeleteAlert() {
    this.isDeleteAlertOpened = !this.isDeleteAlertOpened;
    this.detectChanges();
  }

  public onDeleteCollection() {
    this.collectionsService
      .archive(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.action.emit();
          this.isDeleteAlertOpened = false;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.action.emit();
          this.isDeleteAlertOpened = false;
        },
      });
  }

  public get purchasedLicense(): CollectionLicenseEnum {
    return this._collection.visit_info?.licensed;
  }

  public get canBuy(): boolean {
    return !this.purchasedLicense;
  }

  public getLicenseName(license: CollectionLicenseEnum): string {
    const licenses = {
      [CollectionLicenseEnum.personal]: 'collection-license.personal',
      [CollectionLicenseEnum.professional]: 'collection-license.professional',
      [CollectionLicenseEnum.business]: 'collection-license.business',
    };
    return licenses[license];
  }

  public get canUpgrade(): boolean {
    return !this.isAuthor && !!this.purchasedLicense && this.purchasedLicense !== this.betterLicense;
  }

  public showLicenseSelector() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {}, { view: 'social' } as AuthModalOptions);
      return;
    }
    this.buyCollectionModal = true;
    this.detectChanges();
  }

  public closeLicenseSelector() {
    this.buyCollectionModal = false;
    this.purchaseOperation = null;
    this.detectChanges();
  }

  public setSelectedLicense(license: CollectionLicenseEnum) {
    this.selectedLicense = license;
    this.detectChanges();
  }

  public processPurchase() {
    this.collectionsService
      .purchase(this.collection.id, this.selectedLicense)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (purchaseTransaction) => {
          this.purchaseOperation = purchaseTransaction;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public openComplainForm() {
    this.complainFormOpened = true;
    this.detectChanges();
  }

  public closeComplainForm() {
    this.complainFormOpened = false;
    this.detectChanges();
  }

  public unPublish() {
    this.collectionsService
      .unPublish(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.action.emit();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public publish() {
    this.collectionsService
      .publish(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.action.emit();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public restore() {
    this.collectionsService
      .restore(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.action.emit();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  protected suggestLicense() {
    const price_tiers = this.collection.price_tiers || {};
    let licenseOrder: CollectionLicenseEnum[];
    const isTeacher = AccountService.isAsIfTeacher(this.me);
    const isSchoolOwner = AccountService.hasSegment(this.me, UserSegmentEnum.school_owner);
    if (isSchoolOwner) {
      licenseOrder = [
        CollectionLicenseEnum.business,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
      ];
    } else if (isTeacher) {
      licenseOrder = [
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.business,
      ];
    } else {
      licenseOrder = [
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.business,
      ];
    }
    this.betterLicense = licenseOrder.find((c) => c in price_tiers && price_tiers[c] > 0);
    if (!this.selectedLicense && !!this.purchasedLicense) {
      this.selectedLicense = this.purchasedLicense;
    }

    if (!this.selectedLicense && this.me && this.collection) {
      this.selectedLicense = this.betterLicense;
    }
  }

  public get selectedLicensePrice(): number {
    const price_tiers = this.collection.price_tiers || {};
    return this.selectedLicense ? price_tiers[this.selectedLicense] : null;
  }

  public onModalOpen() {
    if (this.displayMode === 'content') {
      if (this.collectionUrl) {
        this.openInModal.emit(this._collection);
      } else {
        this.router.navigateByUrl(this.editCollectionUrl).then();
      }
    } else {
      this.openInModal.emit(this._collection);
    }
  }

  private prepareCover() {
    this.multiCoverUrls = [];
    if (!!this._collection.__opts) {
      this._collection.__opts.images.map((cover) => {
        this.multiCoverUrls.push(this.prepareStorageUrl(cover.md));
      });
    } else if (this._collection.multi_cover_id) {
      this._collection.multi_cover_id.map((cover_id) => {
        this.multiCoverUrls.push(FilesService.getFileUrlBySize(cover_id));
      });
    }

    this.multiCoverUrls = this.multiCoverUrls.slice(0, 4);
    this.detectChanges();
  }

  public updateCollection() {
    const details: CollectionDetailsType[] = [
      'id',
      'slug',
      'title',
      'excerpt',
      'type',
      'category',
      'language',
      'subject',
      'author_id',
      'description',
      'multi_cover_id',
      'price_tiers',
      'discount',
      'visit_info',
      'stats',
    ];
    this.collectionsService
      .getById(this._collection.id, details)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (collection) => {
          this._collection = collection;
          this.prepareCard();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public closeBuyCollectionModal() {
    this.buyCollectionModal = false;
  }

  public trackByFn(index) {
    return index;
  }
}
